import { fetchTemporaryToken } from 'api'
import { CONFIRM_MODAL_TYPE } from 'component/Modal/ConfirmModal'
import useConfirmModal from 'hooks/useConfirmModal'
import { useToastAlert } from 'hooks/useToastAlert'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { selectTempVideoPageHost } from 'redux/selector/app'
import { isIos26PatchVersion } from 'utils/browser'
import { getDebugInfo } from 'utils/debugUtil'

type Id = string | number

const iosInstalled = (window.navigator as any).standalone === false
export default function usePartVideo() {
  const { openConfirm } = useConfirmModal()
  const tempVideoHost = useSelector(selectTempVideoPageHost)
  const { toast } = useToastAlert()
  const isTempVideoHostNotExists = useMemo(() => {
    return !tempVideoHost
  }, [tempVideoHost])
  const getUrlBase = useCallback(
    async (pageName: string, id: Id) => {
      const token = await fetchTemporaryToken()
      const prefix = 'x-safari-'
      const host = tempVideoHost || window.location.origin
      return `${prefix}${host}/${pageName}/?id=${id}&tmt=${token.temp_access_token}`
    },
    [tempVideoHost]
  )

  const debugAlwaysShowBrowserVideoHint = useMemo(() => getDebugInfo().includes('ALWAYS_SHOW_BROWSER_VIDEO_HINT'), [])

  const isIos26 = useMemo(() => {
    const result = isIos26PatchVersion()
    return result || debugAlwaysShowBrowserVideoHint
  }, [debugAlwaysShowBrowserVideoHint])

  const openInSafariBrowser = useCallback((url: string) => {
    try {
      const newWindow = window.open(url, '_blank')
      if (newWindow) {
        const a = document.createElement('a')
        a.href = url
        a.target = '_blank'
        a.rel = 'noopener noreferrer'
        document.body.appendChild(a)
        a.click()
        a.remove()
      }
    } catch (e) {
      // final fallback: navigate in current window
    }
  }, [])
  /**
   * 開啟長視頻
   */
  const checkVideoLockRef = useMemo(() => ({ current: false }), [])

  const checkVideo = useCallback(
    async (id: Id) => {
      if (checkVideoLockRef.current) {
        // already running
        return true
      }
      checkVideoLockRef.current = true
      try {
        if (isIos26 && !iosInstalled) {
          if (isTempVideoHostNotExists) {
            toast('找不到匹配线路')
            return true
          }
          const url = await getUrlBase('part-video', id)
          openConfirm({
            title: '提示',
            message:
              '亲，您当前系统无法支援播放，将为您开启浏览器进行观看：建议将iOS 设备系统更新至最新版本，可前往【系统设置】进行升级，以获得更流畅的观看体验。',
            confirmText: '前往观看',
            type: CONFIRM_MODAL_TYPE.OK_CANCEL_CUSTOM,
            onConfirm: () => {
              openInSafariBrowser(url)
            },
            cancelText: '取消',
            onCancel: () => {},
          })

          return false
        }
        return true
      } finally {
        checkVideoLockRef.current = false
      }
    },
    [checkVideoLockRef, isIos26, isTempVideoHostNotExists, getUrlBase, openConfirm, toast, openInSafariBrowser]
  )

  /**
   * 開啟短視頻
   */
  const checkShortVideo = useCallback(
    async (id: Id) => {
      if (isIos26 && !iosInstalled) {
        if (isTempVideoHostNotExists) {
          toast('找不到匹配线路')
          return true
        }
        const url = await getUrlBase('part-short', id)
        openConfirm({
          title: '提示',
          message:
            '亲，您当前系统无法支援播放，将为您开启浏览器进行观看：建议将iOS 设备系统更新至最新版本，可前往【系统设置】进行升级，以获得更流畅的观看体验。',
          confirmText: '前往观看',
          onConfirm: () => {
            openInSafariBrowser(url)
          },
          cancelText: '取消',
          onCancel: () => {},
        })

        return false
      }
      return true
    },
    [getUrlBase, isIos26, isTempVideoHostNotExists, openConfirm, openInSafariBrowser, toast]
  )

  /**
   * 開啟短視頻
   */
  const checkShortMasonryVideo = useCallback(
    async ({ id, list, onOk, onCancel }: { id: Id; list: Id[]; onOk?: () => void; onCancel?: () => void }) => {
      if (isIos26 && !iosInstalled) {
        if (isTempVideoHostNotExists) {
          toast('找不到匹配线路')
          return true
        }
        const url = (await getUrlBase('part-short', id)) + '&list=' + list.join(',')
        openConfirm({
          title: '提示',
          message:
            '亲，您当前系统无法支援播放，将为您开启浏览器进行观看：建议将iOS 设备系统更新至最新版本，可前往【系统设置】进行升级，以获得更流畅的观看体验。',
          type: CONFIRM_MODAL_TYPE.OK_CANCEL_CUSTOM,
          confirmText: '浏览器观看',
          onConfirm: () => {
            openInSafariBrowser(url)
            onOk?.()
          },
          cancelText: '观看',
          onCancel: onCancel,
        })

        return false
      }
      return true
    },
    [getUrlBase, isIos26, isTempVideoHostNotExists, openConfirm, openInSafariBrowser, toast]
  )

  return { checkVideo, checkShortVideo, checkShortMasonryVideo }
}
